/**
 * Testing Checklist List Page
 *
 * Displays all testing checklists with filtering by sprint.
 * Shows progress metrics and provides navigation to checklist runner.
 *
 * Module: 3.0 - System Operations (Issues Manager Phase 2)
 * PRD: docs/prds/PRD-ISSUES-MANAGER.md v2.1
 */

import { useState } from "react";
import { Link } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ClipboardList, PlayCircle, AlertCircle, Plus } from "lucide-react";
import { getPassRateColor } from "@/lib/constants/testing-constants";
import { cn } from "@/lib/utils";
import type { TestingChecklist } from "@shared/schema";
import { ChecklistFormDialog } from "@/components/testing/checklist-form-dialog";
import { useToast } from "@/hooks/use-toast";

export default function ChecklistList() {
  const [selectedSprint, setSelectedSprint] = useState<string>("all");
  const [selectedPurpose, setSelectedPurpose] = useState<string>("all"); // Default to all types
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all checklists
  const queryKey = selectedSprint === "all"
    ? ["/api/testing/checklists"]
    : ["/api/testing/checklists", { sprint: selectedSprint }];

  const { data: checklists = [], isLoading } = useQuery<TestingChecklist[]>({
    queryKey,
  });

  // Fetch sprints for filter dropdown
  const { data: sprints = [] } = useQuery<Array<{ id: string; sprintNumber: string }>>({
    queryKey: ["/api/project-manager/sprints"],
  });

  const handleCreateSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/testing/checklists"] });
    setDialogOpen(false);
    toast({
      title: "Checklist created",
      description: "New testing checklist has been created successfully.",
    });
  };

  // Filter checklists client-side
  const filteredChecklists = checklists.filter((checklist) => {
    // Filter by sprint
    if (selectedSprint !== "all" && checklist.sprintId !== selectedSprint) {
      return false;
    }

    // Filter by purpose (testing vs workflow_stage)
    if (selectedPurpose === "testing" && checklist.checklistPurpose === "workflow_stage") {
      return false;
    }
    if (selectedPurpose === "workflow_stage" && checklist.checklistPurpose !== "workflow_stage") {
      return false;
    }

    // Filter by completion status
    if (selectedStatus === "incomplete" && checklist.stepsCompleted === checklist.totalSteps) {
      return false;
    }
    if (selectedStatus === "complete" && checklist.stepsCompleted !== checklist.totalSteps) {
      return false;
    }

    return true;
  });

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title="Testing Checklists"
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Project Manager", href: "/project-manager/dashboard" },
          { label: "Testing Checklists" },
        ]}
        actions={
          <Button onClick={() => setDialogOpen(true)} data-testid="create-checklist-button">
            <Plus className="h-4 w-4 mr-2" />
            Create Checklist
          </Button>
        }
      />

      <div className="flex-1 overflow-auto p-6 pb-20">
        {/* Filters */}
        <div className="mb-6 flex flex-wrap items-center gap-4">
          {/* Sprint Filter */}
          <div className="flex items-center gap-2">
            <label htmlFor="sprint-filter" className="text-sm font-medium">
              Sprint:
            </label>
            <Select value={selectedSprint} onValueChange={setSelectedSprint}>
              <SelectTrigger id="sprint-filter" className="w-[200px]">
                <SelectValue placeholder="All Sprints" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sprints</SelectItem>
                {sprints.map((sprint) => (
                  <SelectItem key={sprint.id} value={sprint.id}>
                    {sprint.sprintNumber}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Purpose Filter */}
          <div className="flex items-center gap-2">
            <label htmlFor="purpose-filter" className="text-sm font-medium">
              Purpose:
            </label>
            <Select value={selectedPurpose} onValueChange={setSelectedPurpose}>
              <SelectTrigger id="purpose-filter" className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="testing">Testing Only</SelectItem>
                <SelectItem value="workflow_stage">Workflow Stages Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Completion Status Filter */}
          <div className="flex items-center gap-2">
            <label htmlFor="status-filter" className="text-sm font-medium">
              Status:
            </label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger id="status-filter" className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="incomplete">Incomplete</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Checklists Grid */}
        {isLoading ? (
          <div className="text-center text-muted-foreground py-12">
            Loading checklists...
          </div>
        ) : filteredChecklists.length === 0 ? (
          <div className="text-center text-muted-foreground py-12">
            <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">No testing checklists found</p>
            <p className="text-sm">
              {selectedSprint === "all"
                ? "Checklists will appear here once created by the Developer Agent"
                : `No checklists found for ${selectedSprint}`}
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredChecklists.map((checklist) => {
              const totalSteps = checklist.totalSteps || 0;
              const stepsCompleted = checklist.stepsCompleted || 0;
              const passRate = typeof checklist.passRate === 'number'
                ? checklist.passRate
                : parseFloat(checklist.passRate || '0');
              const issuesFound = checklist.issuesFound || 0;
              const progressPercentage =
                totalSteps > 0
                  ? (stepsCompleted / totalSteps) * 100
                  : 0;

              return (
                <Card key={checklist.id} className="flex flex-col">
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <Badge variant="outline" className="text-xs">
                        {checklist.checklistNumber}
                      </Badge>
                      <Badge
                        variant="secondary"
                        className="text-xs capitalize"
                      >
                        {checklist.checklistType}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg">{checklist.title}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Sprint: {checklist.sprintId}
                    </p>
                  </CardHeader>
                  <CardContent className="flex-1 space-y-4">
                    {/* Progress Bar */}
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Progress</span>
                        <span className="font-semibold">
                          {stepsCompleted} / {totalSteps}
                        </span>
                      </div>
                      <Progress value={progressPercentage} className="h-2" />
                      <p className="text-xs text-muted-foreground mt-1">
                        {progressPercentage.toFixed(1)}% complete
                      </p>
                    </div>

                    {/* Pass Rate */}
                    {stepsCompleted > 0 && (
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-muted-foreground">Pass Rate</span>
                          <span
                            className={cn(
                              "font-semibold",
                              getPassRateColor(passRate)
                            )}
                          >
                            {passRate.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Issues Found */}
                    {issuesFound > 0 && (
                      <div className="flex items-center gap-2 text-sm">
                        <AlertCircle className="h-4 w-4 text-destructive" />
                        <span>
                          {issuesFound} issue
                          {issuesFound !== 1 ? "s" : ""} found
                        </span>
                      </div>
                    )}

                    {/* Action Button */}
                    <div className="pt-4">
                      <Button asChild className="w-full" size="sm">
                        <Link
                          to={`/project-manager/testing-checklists/${checklist.id}`}
                        >
                          <PlayCircle className="mr-2 h-4 w-4" />
                          {stepsCompleted === totalSteps
                            ? "Go To Testing Checklist"
                            : stepsCompleted === 0
                            ? "Start Testing"
                            : "Continue Testing"}
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Checklist Form Dialog (ISSUE-4 fix) */}
      <ChecklistFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
}
